import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import ConnectionAccordionSection from "../../../../components/view/sections/ConnectionAccordionSection";
import EntityReferenceComponent from "../../../../components/entitiesReferences/EntityReferenceComponent";
import { ViewAccordionField } from "../../../../components/view/fields/ViewAccordion";
import { GetIntellexusDate } from "../../../../components/forms/common/intellexusDate/IntellexusDateField";
import { formatLinksList } from "../../../../components/forms/common/subForms/LinksListField";
import useVersionQuery from "../../../../hooks/query/useVersionQuery";
import { SystemLanguages } from "../../../../shared/enums/shared-enums";
import { Version } from "../../../../types/version";
import { Work } from "../../../../types/work";

interface WorkVersionsViewSectionProps {
  work: Work;
}

const displayText = (value: unknown): string =>
  value !== undefined && value !== null && value !== "" ? String(value) : "N/A";

const getVersionFields = (version: Version): ViewAccordionField[] => {
  const fields: ViewAccordionField[] = [
    {
      label: "Intellexus ID",
      value: <Typography variant="body1">{displayText(version?.published_id)}</Typography>,
    },
    {
      label: "Name",
      value: <Typography variant="body1">{displayText(version?.name)}</Typography>,
    },
    {
      label: "Completeness",
      value: <Typography variant="body1">{displayText(version?.completeness)}</Typography>,
    },
    {
      label: "Version Date",
      value: (
        <Typography variant="body1">
          {version?.version_date ? GetIntellexusDate(version.version_date) : "N/A"}
        </Typography>
      ),
    },
  ];

  if (version?.system_language_type === SystemLanguages.tibetan) {
    fields.push(
      {
        label: "Source Language",
        value: (
          <Typography variant="body1">
            {displayText(version?.source_language)}
          </Typography>
        ),
      },
      {
        label: "Collection",
        value: <Typography variant="body1">{displayText(version?.collection)}</Typography>,
      },
      {
        label: "Vol. Number",
        value: <Typography variant="body1">{displayText(version?.vol_number)}</Typography>,
      },
      {
        label: "Vol. Name",
        value: <Typography variant="body1">{displayText(version?.vol_name)}</Typography>,
      },
      {
        label: "Folio/Page Start",
        value: <Typography variant="body1">{displayText(version?.folio_start)}</Typography>,
      },
      {
        label: "Folio/Page End",
        value: <Typography variant="body1">{displayText(version?.folio_end)}</Typography>,
      }
    );
  }

  if (version?.system_language_type === SystemLanguages.chinese) {
    fields.push(
      {
        label: "Date of Composition",
        value: (
          <Typography variant="body1">
            {version?.date_of_composition
              ? GetIntellexusDate(version.date_of_composition)
              : "N/A"}
          </Typography>
        ),
      },
      {
        label: "Links to Other Databases",
        value: (
          <Typography component="div" variant="body1">
            {formatLinksList(version?.link_to_other_databases ?? [])}
          </Typography>
        ),
      }
    );
  }

  return fields;
};

const WorkVersionsViewSection: React.FC<WorkVersionsViewSectionProps> = ({
  work,
}) => {
  const versionLanguage = work.system_language_type as Version["system_language_type"];
  const { useFetchAll } = useVersionQuery(versionLanguage);
  const { data, isLoading, errorMessage } = useFetchAll({
    filters: {
      system_language_type: versionLanguage,
      work_id: work._key,
    },
  });

  const items = useMemo<Version[]>(() => data?.data ?? [], [data]);

  return (
    <ConnectionAccordionSection<Version>
      title="Versions"
      loading={isLoading}
      error={errorMessage}
      items={items}
      getItemId={(version, index) => version?._key ?? `version-${index}`}
      getItemTitle={(version) => (
        <EntityReferenceComponent entity={version} linkMode="view" />
      )}
      getItemFields={(version) => getVersionFields(version)}
      emptyText="No versions"
    />
  );
};

export default WorkVersionsViewSection;
